<template>
    <div>
        <v-dialog
            v-model="display"
            persistent
            scrollable
            width="950"
            >
            <v-card>
                <v-toolbar flat color="primary" dark class="mb-6" dense>
                    <v-toolbar-title>
                        {{$t("Customer Activity")}}
                    </v-toolbar-title>
                </v-toolbar>
                <v-card-text>
                    <v-form v-model="valid">
                        <v-row>
                            <v-col cols="12" md="7">
                                <v-alert 
                                    dense 
                                    type="warning" 
                                    border="left" 
                                    text
                                    v-if="customerWillBeNotified">
                                    Customer will be notified by <b>SMS</b> about this activity code and due date!
                                </v-alert>
                                <v-combobox
                                    v-model="form.summary"
                                    filled
                                    :label="$t('summary')"
                                    hide-no-data
                                    append-icon
                                    :rules="rules.required">
                                </v-combobox>
                                <v-textarea
                                    v-model="form.details"
                                    hide-details="auto"
                                    rows="6"
                                    :label="$t('details')"
                                    filled
                                    :rules="rules.required">
                                </v-textarea>
                            </v-col>
                            <v-col cols="12" md="4">
                                <customer-avatar
                                    v-if="customer"
                                    :name="customer.name"
                                    :phone="customer.phone">
                                </customer-avatar>
                                <customer-search
                                    v-else
                                    :dense="false"
                                    label="Customer" 
                                    hide-details 
                                    item-value="id" 
                                    item-text="display_name"
                                    v-model="form.customer_id"
                                    class="mb-2"
                                >
                                </customer-search>
                                <v-row>
                                    <v-col cols="12" md="6">
                                        <v-subheader>Activity Type</v-subheader>
                                        <v-radio-group v-model="form.type_id" :rules="rules.required"
                                            v-if="!loading.activityTypes">
                                            <v-radio
                                                v-for="type in types"
                                                :key="type.id"
                                                :value="type.id"
                                                :label="type.name">
                                            </v-radio>
                                        </v-radio-group>
                                        <radio-skeleton v-else :count="4"></radio-skeleton>
                                    </v-col>
                                    <v-col cols="12" md="6">
                                        <v-subheader>Touch Point</v-subheader>
                                        <v-radio-group v-model="form.touch_point_id" :rules="rules.required"
                                            v-if="!loading.activityTypes">
                                            <v-radio
                                                v-for="point in touchPoints"
                                                :key="point.id"
                                                :value="point.id"
                                                :label="point.name">
                                            </v-radio>
                                        </v-radio-group>
                                        <radio-skeleton v-else :count="7"></radio-skeleton>
                                    </v-col>
                                </v-row>
                                <user-search
                                    :dense="false"
                                    label="Assigned To" 
                                    hide-details 
                                    item-value="id" 
                                    item-text="name"
                                    v-model="form.assignee_id"
                                    @click:clear="form.assignee_id = null">
                                </user-search>
                                <v-switch
                                    v-model="form.follow_up"
                                    :label="$t('require_followup')"
                                    hide-details></v-switch>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-btn
                        depressed
                        color="primary"
                        @click="submit"
                        :loading="loading.submit"
                        :disabled="!valid || loading.submit">{{$t("submit")}}</v-btn>
                    <v-btn text @click="display = false">{{$t("cancel")}}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { getDpk } from '../../services/sales';
import { createCustomerActivity, getCustomerActivityTouchPoints, getCustomerActivityTypes } from '../../services/sales/customerActivity';
import RadioSkeleton from '../skeleton/RadioSkeleton.vue';
import CustomerAvatar from './CustomerAvatar.vue';
import UserSearch from '../searchComponents/UserSearch.vue';
import CustomerSearch from '../searchComponents/CustomerSearch.vue';


export default {
  components: { 
      CustomerAvatar,
      RadioSkeleton,
      UserSearch,
      CustomerSearch,
    },
    name: "CustomerActivityEdit",
    props: {
        customer: {
            type: Object,
            required: false
        },
        value: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            valid: false,
            dpk: null,
            form: {
                customer_id: null,
                assignee_id: null,
                summary: null,
                details: null,
                follow_up: false,
                type_id: null,
                touch_point_id: null
            },
            loading: {
                submit: false,
                touchPoints:false,
                activityTypes:false
            },
            rules: {
                required: [
                    v => !!v || this.$t("required")
                ]
            },
            types: [],
            touchPoints: []
        }
    },
    computed: {
        display: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit("input", value)
            }
        },
        customerWillBeNotified() {
            if(this.types.length === 0) return false;
            let type = this.types.find(each => each.id == this.form.type_id);
            if(type && type.sms == 1) return true;
            else return false;
        }
    },
    methods: {
        submit() {
            this.loading.submit = true;
            if(this.customer) this.form.customer_id = this.customer.id;
            createCustomerActivity({
                form: this.form,
                dpk: this.dpk
            }).then((response) => {
                this.display = false;
                this.$emit("created", response.data.record);
            }).finally(() => {
                this.loading.submit = false;
            });
        },
        fetchDpkFromSales() {
            getDpk().then(response => this.dpk = response.data.key);
        },
        fetchCustomerActivityTypes() {
            this.loading.activityTypes = true;
            getCustomerActivityTypes()
                .then(response => this.types = response.data)
                .finally(() => this.loading.activityTypes = false);
        },
         fetchCustomerActivityTouchPoints() {
            this.loading.touchPoints = true;
            getCustomerActivityTouchPoints()
                .then(response => this.touchPoints = response.data)
                .finally(() => this.loading.touchPoints = false);
        }
    },
    created() {
        this.fetchDpkFromSales();
        this.fetchCustomerActivityTypes();
        this.fetchCustomerActivityTouchPoints();
    }
}
</script>
